import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Clock, X, FileEdit } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { cn } from '../lib/utils';
import { TimesheetState } from '../services/timesheetService';

interface ApprovalStatusBadgeProps {
  state: TimesheetState;
  rejectionReason?: string;
  compact?: boolean;
  className?: string;
}

const stateConfig: Record<TimesheetState, {
  label: string;
  icon: React.ElementType;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
}> = {
  draft: {
    label: 'timesheet.states.draft',
    icon: FileEdit,
    variant: 'secondary',
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  },
  in_approval: {
    label: 'timesheet.states.in_approval',
    icon: Clock,
    variant: 'outline',
    className: 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700',
  },
  approved: {
    label: 'timesheet.states.approved',
    icon: Check,
    variant: 'default',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  rejected: {
    label: 'timesheet.states.rejected',
    icon: X,
    variant: 'destructive',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
};

export const ApprovalStatusBadge: React.FC<ApprovalStatusBadgeProps> = ({
  state,
  rejectionReason,
  compact = false,
  className,
}) => {
  const { t } = useTranslation();
  const config = stateConfig[state];
  const Icon = config.icon;

  const badge = (
    <Badge
      variant={config.variant}
      className={cn(config.className, 'gap-1', className)}
    >
      <Icon className="h-3 w-3" />
      {!compact && <span>{t(config.label)}</span>}
    </Badge>
  );

  if (state === 'rejected' && rejectionReason) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">{t('approvals.rejectionReason', 'Reason')}:</p>
            <p>{rejectionReason}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
};

export default ApprovalStatusBadge;
